// client/src/components/pages/LexiStar/Routes/VersionsPanel.jsx
//
// A route's immutable versions, newest first (FR-ROUT-004). Trips pin the
// version in force at dispatch, so nothing here edits a version: "Compare"
// draws an older one on the map; "Start from this version" copies it into the
// editor so saving makes it the next version (a rollback that keeps history).

import React from 'react';
import { formatAppDateTime } from '../../../../utils/dateUtils';
import { formatKm, formatMinutes } from './routeGeometry';

const SOURCE_LABELS = {
  engine: 'Routing engine',
  golden_run: 'Golden run',
  manual: 'Manual',
  import: 'Import',
};

export default function VersionsPanel({
  versions,
  currentVersion,
  compareVersion,
  onCompare,
  onStartFrom,
  canChange,
}) {
  if (!versions.length) {
    return (
      <p className="text-sm text-slate-600">
        No versions yet. Generate or load a line on the Edit tab and save it as version 1.
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {versions.map((v) => {
        const isCurrent = v.version === currentVersion;
        const comparing = compareVersion === v.version;
        return (
          <li
            key={v.version}
            className={`rounded-lg border p-3 ${isCurrent ? 'border-primary-300 bg-primary-50/40' : 'border-slate-200'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">
                Version {v.version}
                {isCurrent && (
                  <span className="ml-2 text-[11px] font-medium text-primary-700 uppercase tracking-wide">
                    current
                  </span>
                )}
              </p>
              <span className="text-xs text-slate-500">{SOURCE_LABELS[v.source] || v.source}</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              {formatAppDateTime(v.created_at)}
              {v.created_by ? ` · ${v.created_by}` : ''}
            </p>
            <p className="mt-1.5 text-xs text-slate-700 tabular-nums">
              {formatKm(v.planned_km)} · {formatMinutes(v.planned_min)} · corridor {v.corridor_m} m
              · {(v.permitted_stop_geofence_ids || []).length} stops ·{' '}
              {(v.required_weighbridge_geofence_ids || []).length} weighbridges
            </p>
            {v.notes && <p className="mt-1 text-xs text-slate-600 italic">“{v.notes}”</p>}
            {!isCurrent && (
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => onCompare(comparing ? null : v.version)}
                  aria-pressed={comparing}
                  className={`px-2 py-1 rounded-md text-xs font-medium ${
                    comparing
                      ? 'bg-slate-700 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {comparing ? 'Hide on map' : 'Compare on map'}
                </button>
                {canChange && (
                  <button
                    type="button"
                    onClick={() => onStartFrom(v)}
                    className="px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    Start from this version
                  </button>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
